/**
 * ApiClient.ts
 *
 * 백엔드 REST API(/api) 클라이언트.
 * - 인증: Authorization: Bearer <sessionToken>
 * - 세션 토큰은 localStorage('terra_anima_session_token')에 저장
 * - 활동 로그 및 캐릭터 CRUD 제공
 */

import { SavedCharacterSlot } from './AccountSaveSystem';
import { ActivityType } from './ActivityLog';

const TOKEN_KEY = 'terra_anima_session_token';

export interface ApiAccount {
  id: number;
  provider: 'discord' | 'google';
  email: string | null;
  name: string | null;
  avatarUrl: string | null;
}

export interface LoginResult {
  token: string;
  isNew: boolean;
  account: ApiAccount;
}

export class ApiClient {
  public static getToken(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  }

  public static setToken(token: string | null): void {
    try {
      if (token) localStorage.setItem(TOKEN_KEY, token);
      else localStorage.removeItem(TOKEN_KEY);
    } catch (e) {
      console.warn('[ApiClient] Failed to persist session token', e);
    }
  }

  private static authHeaders(extra?: Record<string, string>): Record<string, string> {
    const h: Record<string, string> = { ...extra };
    const tok = this.getToken();
    if (tok) h['Authorization'] = `Bearer ${tok}`;
    return h;
  }

  public static async loginWithProvider(provider: 'discord' | 'google', accessToken: string): Promise<LoginResult> {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ provider, accessToken }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'login failed' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    const data: LoginResult = await res.json();
    this.setToken(data.token);
    return data;
  }

  public static async logout(): Promise<void> {
    try {
      if (this.getToken()) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: this.authHeaders(),
        });
      }
    } catch (e) {
      console.warn('[ApiClient] Logout failed', e);
    } finally {
      this.setToken(null);
    }
  }

  public static async getMyAccount(): Promise<ApiAccount | null> {
    if (!this.getToken()) return null;
    try {
      const res = await fetch('/api/me', { headers: this.authHeaders() });
      if (res.status === 401) {
        this.setToken(null);
        return null;
      }
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }

  public static async getCharacters(): Promise<SavedCharacterSlot[]> {
    if (!this.getToken()) return [];
    const res = await fetch('/api/characters', { headers: this.authHeaders() });
    if (!res.ok) {
      if (res.status === 401) this.setToken(null);
      return [];
    }
    return await res.json();
  }

  public static async createCharacter(char: Partial<SavedCharacterSlot> & { name: string }): Promise<SavedCharacterSlot> {
    const res = await fetch('/api/characters', {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(char),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'create failed' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    return await res.json();
  }

  public static async updateCharacter(id: string, updates: Partial<SavedCharacterSlot>): Promise<void> {
    const res = await fetch(`/api/characters/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(updates),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'update failed' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
  }

  public static async deleteCharacter(id: string): Promise<void> {
    const res = await fetch(`/api/characters/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: this.authHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'delete failed' }));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
  }

  public static async logActivity(type: ActivityType, message: string, character?: string): Promise<void> {
    if (!this.getToken()) return;
    try {
      await fetch('/api/log', {
        method: 'POST',
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ type, message, character }),
      });
    } catch (e) {
      console.warn('[ApiClient] Failed to report activity log to server', e);
    }
  }
}
